export const personalInfo = {
  name: "Dibyataru Chakraborty",
  role: "Security Analyst",
  label: "SECURITY ANALYST • CYBERSECURITY",
  headline: "Security Analyst building practical security solutions.",
  subheadline:
    "I work across security operations, SIEM implementation, vulnerability assessment, system hardening, malware analysis, and security monitoring — turning security requirements into practical security solutions.",
  status: "Currently Working as Security Analyst",
  location: "Kolkata, India",
  email: "dibyataruchakraborty@gmail.com",
  linkedin: "https://www.linkedin.com/in/dibyataru-chakraborty/",
  github: "https://github.com/Dibyataru-Chakraborty",
  tryhackme: "https://tryhackme.com/p/XHunter101",
  tryhackmeBadge: "https://tryhackme-badges.s3.amazonaws.com/XHunter101.png",
  zcal: "https://zcal.co/dibyataru",
  zcalEmbed: "https://zcal.co/dibyataru?embed=1&embedType=iframe",
  avatar: "./icon/IMG_20240112_212603_298.jpg",
};

export const capabilities = [
  {
    id: "siem",
    title: "SIEM & Monitoring",
    description: "Centralized telemetry, rule tuning, and incident detection pipeline.",
    items: ["Wazuh SIEM", "Log Analysis", "Alert Investigation"],
    icon: "shield-halved",
    accent: "#38BDF8",
  },
  {
    id: "appsec",
    title: "Application Security",
    description: "Proactive vulnerability assessment and web/API penetration testing.",
    items: ["VAPT", "Web Security", "API Security"],
    icon: "code-merge",
    accent: "#818CF8",
  },
  {
    id: "infrasec",
    title: "Infrastructure Security",
    description: "System baseline compliance, policy enforcement, and defense-in-depth.",
    items: ["System Hardening", "Endpoint Security", "Active Directory"],
    icon: "server",
    accent: "#34D399",
  },
  {
    id: "research",
    title: "Security Research",
    description: "Threat hunting, OSINT reconnaissance, and attack surface discovery.",
    items: ["OSINT", "Investigation", "Reconnaissance"],
    icon: "magnifying-glass",
    accent: "#F472B6",
  },
];

export const experience = [
  {
    role: "Security Analyst",
    type: "Professional Experience",
    summary:
      "Hands-on security operations, SIEM engineering, vulnerability assessments, and defensive infrastructure hardening across hybrid environments.",
    responsibilities: [
      "Designed and deployed centralized Wazuh SIEM monitoring and endpoint agent architecture across endpoints and servers.",
      "Integrated network firewall and security appliance syslogs for unified visibility and correlation.",
      "Conducted Vulnerability Assessment and Penetration Testing (VAPT) following OWASP Top 10 guidelines.",
      "Performed initial malware triage and artifact analysis to identify persistence mechanisms and extract indicators of compromise (IOCs).",
      "Collaborated with technical stakeholders to validate vulnerabilities and guide effective remediation workflows.",
      "Hardened Windows and Linux systems according to industry security baselines, configuring Group Policy (GPO) and ADMX templates.",
      "Prepared client-facing technical security assessments, executive risk summaries, and compliance documentation.",
      "Conducted cybersecurity awareness, OSINT methodologies, and security monitoring training sessions.",
    ],
  },
];

export const recognitions = [
  {
    title: "NCIIPC RVDP",
    organization: "National Critical Information Infrastructure Protection Centre",
    detail: "Responsible Vulnerability Disclosure Program",
    count: "4",
    type: "Acknowledgement",
    badgeColor: "#38BDF8",
  },
  {
    title: "US Dept of Health & Human Services",
    organization: "United States Government",
    detail: "Responsible Disclosure Hall of Fame",
    count: "1",
    type: "Hall Of Fame",
    badgeColor: "#22C55E",
    url: "https://hhs.responsibledisclosure.com/hc/en-us/articles/1500000280921-Acknowledgments#:~:text=Dibyataru%20Chakraborty",
  },
  {
    title: "Ferrari",
    organization: "Ferrari S.p.A.",
    detail: "Vulnerability Disclosure Recognition",
    count: "1",
    type: "Hall Of Fame",
    badgeColor: "#EF4444",
    url: "https://www.ferrari.com/en-IN/hall-of-fame-responsible-disclosure-programme#:~:text=Dibyataru%20Chakraborty",
  },
];

export const cveAchievement = {
  label: "SECURITY ACHIEVEMENT",
  cveId: "CVE-2026-102628",
  role: "CVE Contributor / Vulnerability Researcher",
  title: "CVE-2026-102628 — Published CVE Record",
  description:
    "Discovered and responsibly disclosed a security vulnerability that was assigned CVE-2026-102628.",
  url: "https://www.cve.org/CVERecord?id=CVE-2026-102628",
};

export const featuredProjects = [
  {
    id: "wazuh-siem",
    name: "Wazuh SIEM Implementation & Security Monitoring",
    category: "Security Operations & SIEM",
    shortDescription:
      "Implemented centralized security monitoring and log visibility across endpoints and infrastructure using Wazuh.",
    problem:
      "Lack of centralized log visibility across endpoints, unmonitored infrastructure telemetry, delayed detection of anomalous behavior, and absence of standardized data retention tiers.",
    contribution:
      "Engineered Wazuh Manager and Indexer architecture, deployed and enrolled multi-platform agents, configured firewall syslog ingestion, and established a strict multi-tier index retention lifecycle.",
    technologies: [
      "Wazuh Manager",
      "Wazuh Agents",
      "Wazuh Indexer",
      "Wazuh Dashboard",
      "Syslog",
      "Linux",
      "Windows Server",
    ],
    architecture: [
      { name: "Endpoints", desc: "Windows, Linux workstations & servers" },
      { name: "Wazuh Agents", desc: "Local log collection & rootcheck telemetry" },
      { name: "Wazuh Manager", desc: "Decoders, custom rules & analysis engine" },
      { name: "Wazuh Indexer", desc: "High-performance search & storage cluster" },
      { name: "Dashboard", desc: "Visualization, triage & alert dashboards" },
      { name: "Security Monitoring", desc: "Proactive threat detection & response" },
    ],
    retentionPolicy: [
      { tier: "HOT", duration: "0–7 days", desc: "Active indexing and immediate real-time query search.", color: "#EF4444" },
      { tier: "WARM", duration: "7–15 days", desc: "Read-only optimized storage for triage & compliance review.", color: "#F59E0B" },
      { tier: "DELETE", duration: "15+ days", desc: "Automated index purging ensuring storage efficiency.", color: "#6B7280" },
    ],
    caseStudy: {
      overview:
        "Designed and implemented an enterprise-grade Wazuh SIEM deployment to aggregate events, monitor system integrity, and establish continuous security visibility across endpoints and servers.",
      challenge:
        "Infrastructure components generated fragmented logs without unified analysis. Security events remained siloed, making incident correlation and timely detection of suspicious activities difficult without overwhelming storage.",
      approach:
        "Built a scalable Wazuh architecture with clear separation between ingestion, analysis, and indexing tiers. Enforced a multi-tier index management policy (Hot: 0-7d, Warm: 7-15d, Delete: 15d+) to balance forensic depth with storage economy.",
      implementation:
        "Configured secure TLS communication between Wazuh agents and manager. Integrated external network firewalls via remote syslog. Tailored custom decoder and rule files to filter false positives and surface actionable alerts.",
      toolsUsed: ["Wazuh Manager", "Wazuh Agent", "OpenSearch Indexer", "Syslog-ng", "Linux", "PowerShell"],
      findings:
        "Identified brute-force authentication attempts against administrative services, unmonitored endpoint privilege escalations, and firewall anomalies.",
      remediation:
        "Tuned alerting rules, automated IP blocking via active response where appropriate, and instituted strict access control over log retention indices.",
      outcome:
        "Achieved 100% centralized log visibility across target systems, reduced alert investigation times, and established a dependable security monitoring foundation.",
      lessonsLearned:
        "Effective log parsing rules and pre-filtering at the agent layer are vital to prevent indexer bottlenecks and maintain high-speed searchability during investigations.",
    },
  },
  {
    id: "vapt-framework",
    name: "Vulnerability Assessment & Penetration Testing (VAPT)",
    category: "Application & Network Security",
    shortDescription:
      "Systematic security assessments across web applications and network services to identify, validate, and remediate high-risk vulnerabilities.",
    problem:
      "Web applications exposed to potential OWASP Top 10 vulnerabilities, unauthorized API endpoints, weak session management, and configuration drift.",
    contribution:
      "Conducted black-box and grey-box security evaluations, mapped attack surfaces, safely validated exploitable flaws, and authored comprehensive remediation advisories.",
    technologies: [
      "Burp Suite Professional",
      "OWASP ZAP",
      "Nmap",
      "Postman",
      "Python",
      "Kali Linux",
    ],
    workflow: [
      "Reconnaissance",
      "Attack Surface Mapping",
      "Security Testing",
      "Vulnerability Identification",
      "Risk Assessment",
      "Remediation",
      "Retesting",
    ],
    focusAreas: [
      "OWASP Top 10",
      "Authentication Mechanisms",
      "Authorization & Broken Object Level Auth",
      "Session Management & Tokens",
      "Input Validation & Injection Flaws",
      "API Security & Rate Limiting",
      "Security Headers & CORS Policies",
      "TLS/SSL Protocols & Cipher Suites",
      "Server & Container Configuration Security",
    ],
    caseStudy: {
      overview:
        "Conducted end-to-end vulnerability assessments and penetration tests across customer-facing web applications and RESTful APIs, adhering to OWASP and PTES standards.",
      challenge:
        "Complex client-side architectures and API services requiring in-depth manual logic testing beyond basic automated scanners to discover business logic flaws and broken access controls.",
      approach:
        "Employed a phased testing methodology: initial OSINT and surface mapping, followed by targeted API fuzzing, privilege escalation testing, and credential boundary verification.",
      implementation:
        "Scripted custom testing helpers in Python; intercepted and analyzed complex API request flows in Burp Suite; validated role separation between unprivileged and administrative tiers.",
      toolsUsed: ["Burp Suite", "OWASP ZAP", "Nmap", "Postman", "Curl", "Python"],
      findings:
        "Uncovered authorization bypasses in API endpoints, missing security headers, session fixation risks, and misconfigured CORS policies.",
      remediation:
        "Provided developers with code-level remediation steps, validated input sanitation routines, and assisted in configuring strict HSTS and CSP directives.",
      outcome:
        "Neutralized critical attack vectors prior to production exposure and established regular security retesting cycles.",
      lessonsLearned:
        "Automated scanners miss contextual business logic flaws; thorough manual verification of authorization matrices is essential for true application resilience.",
    },
  },
  {
    id: "system-hardening",
    name: "System Hardening & Baseline Compliance",
    category: "Infrastructure Security",
    shortDescription:
      "Hardened operating system baselines, Group Policy security objects, and endpoint configurations to eliminate initial compromise vectors.",
    problem:
      "Default Windows and Linux deployments leaving legacy protocols enabled (e.g. SMBv1, NTLMv1), excessive user privileges, weak ciphers, and insufficient audit logging.",
    contribution:
      "Configured security baselines utilizing Group Policy (ADMX/ADML), enforced modern TLS configurations, established granular audit logging policies, and verified hardening compliance.",
    technologies: [
      "Windows Server",
      "Active Directory",
      "Group Policy (GPO)",
      "ADMX / ADML Templates",
      "PowerShell",
      "Linux Hardening",
    ],
    stages: [
      {
        stage: "Before",
        status: "High Attack Surface",
        details: "Default service configurations, legacy protocols active, generic audit logs, unconstrained local administrator rights.",
        color: "#EF4444",
      },
      {
        stage: "Hardening",
        status: "Policy & Baselines",
        details: "GPO baseline deployment, ADMX template configuration, TLS 1.2/1.3 enforcement, disabling LLMNR/NetBIOS, advanced auditing.",
        color: "#F59E0B",
      },
      {
        stage: "After",
        status: "Resilient & Audited",
        details: "Enforced least-privilege access, verified CIS-aligned configurations, centralized security event logging, reduced lateral movement risks.",
        color: "#22C55E",
      },
    ],
    caseStudy: {
      overview:
        "Engineered and rolled out baseline system hardening for endpoints and server infrastructure, minimizing the attack surface through standardized Group Policy Objects and configuration audits.",
      challenge:
        "Balancing rigorous security baselines against operational usability, avoiding service interruption while disabling legacy protocols and restricting administrative privileges.",
      approach:
        "Phased audit-and-enforce model: documented existing configurations, deployed baselines to test groups, analyzed impact on legacy workflows, and transitioned into strict enforcement.",
      implementation:
        "Utilized Microsoft Security Compliance Toolkit and custom ADMX templates. Enforced BitLocker policies, Credential Guard prerequisites, disabled SMBv1/NTLMv1, and tuned Windows Event Log channel capacities.",
      toolsUsed: ["Group Policy Management", "PowerShell", "Security Compliance Toolkit", "Auditpol", "Sysmon"],
      findings:
        "Discovered orphaned administrative accounts, unencrypted local communications, and blind spots in process creation logging.",
      remediation:
        "Enforced tiered administration, restricted local admin membership via GPO Restricted Groups, and activated Command Line Process Auditing (Event ID 4688).",
      outcome:
        "Substantially reduced the host attack surface, met security baseline compliance objectives, and provided telemetry for SIEM ingestion.",
      lessonsLearned:
        "Hardening is an ongoing lifecycle, not a one-time script; centralizing policy definitions via version-controlled GPOs ensures consistency across the fleet.",
    },
  },
  {
    id: "osint-investigation",
    name: "OSINT & Cyber Investigation Framework",
    category: "Threat Intelligence & Research",
    shortDescription:
      "Structured open-source intelligence gathering and data correlation for external attack surface assessment and digital footprint analysis.",
    problem:
      "Exposed digital assets, public credential leaks, misconfigured cloud storage, and unmonitored external footprints exposing organizations to reconnaissance.",
    contribution:
      "Created structured OSINT workflows to map organizational assets, identify exposed data, and conduct targeted threat research.",
    technologies: [
      "OSINT Frameworks",
      "Shodan",
      "Maltego",
      "Whois & DNS Analysis",
      "Python Automation",
      "Metadata Extraction",
    ],
    workflow: [
      "Information Gathering",
      "OSINT Sources",
      "Data Correlation",
      "Investigation",
      "Findings",
    ],
    caseStudy: {
      overview:
        "Developed structured OSINT investigative methodologies used in threat reconnaissance, external attack surface reviews, and security awareness training workshops.",
      challenge:
        "Navigating vast quantities of public unstructured data to reliably isolate actionable threat indicators without producing false positives or breaching ethical boundaries.",
      approach:
        "Layered passive reconnaissance pipeline: passive DNS aggregation, certificate transparency log inspection, search engine dorking, and metadata forensic inspection.",
      implementation:
        "Employed automated Python scripts to correlate domain ownership, exposed services, subdomains, and public source repositories without generating invasive target traffic.",
      toolsUsed: ["Shodan", "Censys", "Certificate Transparency Logs", "TheHarvester", "Maltego", "Python"],
      findings:
        "Uncovered forgotten staging subdomains, exposed test endpoints, and legacy DNS records pointing to decommissioned hosts.",
      remediation:
        "Assisted asset owners with decommissioning legacy DNS CNAMEs to prevent subdomain takeovers, and tightening external exposure.",
      outcome:
        "Delivered clear digital footprint audits and authored training material to educate teams on mitigating external exposure.",
      lessonsLearned:
        "Organizations consistently leak critical architectural intelligence through non-production domains and public metadata; disciplined passive reconnaissance is vital.",
    },
  },
];

export const additionalProjects = [
  {
    id: "digi-samurai",
    name: "Digi Samurai",
    category: "Information Security Platform",
    date: "Dec 2024",
    image: "./icon/digisamurai.png",
    description:
      "Delivers comprehensive, simplified insights across information security and IT domains. Transforms complex technical challenges into actionable strategies, helping businesses navigate the digital threat landscape.",
    technologies: ["Next.js", "Tailwind CSS", "Ant Design", "InfoSec Content"],
  },
  {
    id: "kanika-diagnostic",
    name: "Kanika Diagnostic",
    category: "Healthcare Web Application",
    date: "Feb 2024",
    image: "./icon/kdiagnostic.vercel.app_.png",
    description:
      "Online medicine ordering and diagnostic assistance platform with fast home delivery and secure customer management.",
    technologies: ["Bootstrap", "Firebase", "Ant Design", "JavaScript"],
  },
  {
    id: "jalangi-polymer",
    name: "Jalangi Polymer",
    category: "Enterprise Billing System",
    date: "Jan 2024",
    image: "./icon/billing-system-2024.vercel.app_.png",
    description:
      "Online billing system with automated stock maintenance, inventory tracking, and monthly profit/loss financial analytics.",
    technologies: ["Bootstrap", "Firebase", "Ant Design", "JavaScript"],
  },
  {
    id: "qrpay",
    name: "Qrpay",
    category: "Desktop Utility",
    date: "Sep 2022",
    image: "./icon/qrpay.jpg",
    description:
      "Desktop payment utility for retail environments with dynamic QR code generation and transaction receipt logging.",
    technologies: ["Python", "Qt 5", "SQLite"],
  },
  {
    id: "jaxon",
    name: "Jaxon Assistant",
    category: "Automation & Speech",
    date: "Feb 2022",
    image: "./icon/jaxon.jpg",
    description:
      "Personal desktop assistant with predefined command modules, task automation routines, and bilingual speech support (English & Bengali).",
    technologies: ["Python", "SpeechRecognition", "PyAudio"],
  },
];

export const skillsGrouped = [
  {
    category: "Security Operations",
    accent: "#38BDF8",
    skills: ["Wazuh", "SIEM", "Log Analysis", "Security Monitoring", "Alert Investigation", "Malware Analysis"],
  },
  {
    category: "Application Security",
    accent: "#818CF8",
    skills: ["VAPT", "Web Application Security", "API Security", "OWASP", "Vulnerability Assessment"],
  },
  {
    category: "Infrastructure Security",
    accent: "#34D399",
    skills: ["Windows", "Linux", "Active Directory", "System Hardening", "Endpoint Security", "Firewall"],
  },
  {
    category: "Security Research",
    accent: "#F472B6",
    skills: ["OSINT", "Reconnaissance", "Cyber Investigation", "Malware Analysis"],
  },
  {
    category: "Data & Infrastructure",
    accent: "#FBBF24",
    skills: ["PostgreSQL", "MongoDB", "Docker", "Networking", "TLS/SSL"],
  },
  {
    category: "Software Engineering & R&D",
    accent: "#60A5FA",
    skills: ["React", "Django", "Next.js", "Qt 5", "Git & GitHub", "R&D Prototyping"],
  },
];

export const securityToolbox = [
  { name: "Wazuh", role: "SIEM & Host Monitoring", icon: "./icon/burpsuite.png", badge: "Primary SIEM" },
  { name: "Burp Suite", role: "Web Vulnerability Testing", icon: "./icon/burpsuite.png", badge: "AppSec" },
  { name: "OWASP ZAP", role: "Automated Web Scanner", icon: "./icon/zap.png", badge: "DAST" },
  { name: "Nmap", role: "Network Discovery & Auditing", icon: "./icon/nmap.png", badge: "Recon" },
  { name: "Wireshark", role: "Packet & Protocol Analysis", icon: "./icon/wireshark.png", badge: "Traffic" },
  { name: "Kali Linux", role: "Offensive Security OS", icon: "./icon/kali-linux.png", badge: "OS" },
  { name: "Malware Analysis", role: "Triage & IOC Extraction", icon: "./icon/burpsuite.png", badge: "Analysis" },
  { name: "Active Directory", role: "Identity & Group Policy", icon: "./icon/vscode.svg", badge: "Infrastructure" },
  { name: "Windows Server", role: "OS Hardening & Baselines", icon: "./icon/vscode.svg", badge: "System" },
  { name: "Linux", role: "Servers, Daemons & Security", icon: "./icon/kali-linux.png", badge: "System" },
  { name: "Docker", role: "Containerization & Isolation", icon: "./icon/vscode.svg", badge: "Containers" },
  { name: "Python", role: "Security Tooling & Automation", icon: "./icon/python.svg", badge: "Scripting" },
  { name: "React / Next.js", role: "Web Architecture & UI", icon: "./icon/react-js.svg", badge: "Frontend" },
  { name: "Django", role: "Backend Web Framework", icon: "./icon/django.svg", badge: "Backend" },
  { name: "Qt 5", role: "Desktop Systems & GUI", icon: "./icon/qt.svg", badge: "Desktop" },
  { name: "Git & GitHub", role: "VCS, Collaboration & R&D", icon: "./icon/git.svg", badge: "DevOps" },
  { name: "PostgreSQL / MySQL", role: "Relational Data Stores", icon: "./icon/mysql.svg", badge: "Databases" },
];

export const certifications = [
  {
    title: "Troubleshooting and Debugging Techniques",
    issuer: "Google / Coursera",
    image: "./Certificate/Troubleshooting and Debugging Techniques.jpg",
    url: "https://coursera.org/share/763fc923fb7b07aee5ced09081b5b34b",
    status: "Verified Credential",
  },
  {
    title: "Introduction to Git and GitHub",
    issuer: "Google / Coursera",
    image: "./Certificate/Introduction to Git and GitHub.jpg",
    url: "https://coursera.org/share/045060a3709ee6a66a632ffe62b48758",
    status: "Verified Credential",
  },
  {
    title: "Introduction to Cybersecurity Tools & Cyber Attacks",
    issuer: "IBM / Coursera",
    image: "./Certificate/Introduction to Cybersecurity Tools & Cyber Attacks.jpg",
    url: "https://coursera.org/share/68a3dc9e252134fca917b8fe4b2aaaf9",
    status: "Verified Credential",
  },
  {
    title: "Postman API Fundamentals Student Expert",
    issuer: "Postman / Badgr",
    image: "./Certificate/Postman.png",
    url: "https://badgr.com/public/assertions/ZL8fb1ehQD2fD69PhugeZA?identity__email=dibyataruchakraborty@gmail.com",
    status: "Verified Credential",
  },
];

export const knowledgeSharing = [
  {
    topic: "Open Source Intelligence (OSINT) & Digital Footprinting",
    description:
      "Structured lecture and practical demonstrations on passive reconnaissance methodologies, credential breach analysis, and mitigating public exposure.",
    audience: "Students & Security Enthusiasts",
    type: "Workshop & Training",
  },
  {
    topic: "Cyber Investigation & Threat Awareness",
    description:
      "Explored standard cyber investigation procedures, incident response timelines, phishing mechanics, and modern attack vectors.",
    audience: "Academic & Tech Community",
    type: "Presentation",
  },
  {
    topic: "Defensive Security Fundamentals & SIEM Architecture",
    description:
      "Session covering defensive observability, the role of Wazuh in threat detection, and baseline system hardening strategies.",
    audience: "Developers & IT Administrators",
    type: "Technical Session",
  },
];

export const education = [
  {
    degree: "Master of Computer Applications (MCA)",
    institution: "Sister Nivedita University",
    period: "2023 – 2025",
    status: "Completed",
    description:
      "Advanced studies focusing on computer science, cybersecurity, network architecture, and distributed enterprise systems.",
  },
  {
    degree: "Bachelor of Computer Applications (BCA)",
    institution: "MAKAUT (GCST)",
    period: "2020 – 2023",
    status: "Completed",
    description:
      "Foundation in systems programming, data structures, algorithms, database management, and network protocols.",
  },
];

